import { withInfoType } from '@/app/considerations/appliesToPublishCheck';
import { FieldDataTypeBadge } from '@/components/badges/FieldDataTypeBadge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useAppliesToGuard } from '@/db/useAppliesToGuard';
import { useBusyAction } from '@/db/useBusyAction';
import { infoTypesState } from '@/state';
import { useLoadableAtomValue } from '@/state/loadableHooks';
import logger from '@/util/logger';
import { InfoTypeDao } from '@rock/db-admin';
import type { InfoType, InfoTypeId, SpaceId } from '@rock/types';
import { type FC, useState } from 'react';
import { toast } from 'sonner';
import { InfoTypeDisplay } from '../../nodes/NodeUI';
import InfoTypeForm from '../itemType/InfoTypeForm';

const log = logger('InfoTypesTable');
const infoTypeDao = new InfoTypeDao();

export default function InfoTypesTable({ spaceId }: { spaceId: SpaceId }) {
    const [types, typesLoadable] = useLoadableAtomValue(infoTypesState(spaceId), []);
    const [editingTypeId, setEditingTypeId] = useState<undefined | null | InfoTypeId>();
    const { busy: loading, run } = useBusyAction(log, { spaceId });
    const guard = useAppliesToGuard();

    async function saveInfoType(type: InfoType) {
        if (type.dataType === 'select' && (!type.options || type.options.length === 0)) {
            toast.error('Select fields require at least one option');
            return;
        }
        await run(`Save ${type.id}`, async () => {
            await guard(`Saving ${type.id}`, (catalog) => withInfoType(catalog, type.id, type));
            await infoTypeDao.setInfoType(type.id, type);

            toast.success('Info Type Updated');
            log.info(`Updated Info Type: ${type.id} for Space: ${spaceId}`);
            setEditingTypeId(undefined);
        });
    }

    async function deleteInfoType() {
        if (!editingTypeId) return;
        await run(`Delete ${editingTypeId}`, async () => {
            await guard(`Deleting ${editingTypeId}`, (catalog) => withInfoType(catalog, editingTypeId, undefined));
            await infoTypeDao.delete(editingTypeId);
            log.info(`Deleted Info Type: ${editingTypeId} for Space: ${spaceId}`);
            setEditingTypeId(undefined);
        });
    }

    if (typesLoadable.state === 'hasData') {
        const editingType = types.find((t) => t.id === editingTypeId) || null;
        const showEditForm = editingTypeId || editingTypeId === null;

        return (
            <div>
                <DisplayInfoTypes
                    spaceId={spaceId}
                    types={types}
                    onAddNew={() => setEditingTypeId(null)}
                    onEdit={(typeId) => setEditingTypeId(typeId)}
                />

                {showEditForm && (
                    <Dialog open onOpenChange={() => setEditingTypeId(undefined)}>
                        <DialogContent className="max-h-[80vh] overflow-y-auto">
                            <DialogHeader>
                                <DialogTitle>{editingType?.title || 'Create New Info Type'}</DialogTitle>
                                <DialogDescription className="m-2">
                                    Enter or update the details for the Info Type.
                                </DialogDescription>
                            </DialogHeader>
                            <InfoTypeForm
                                spaceId={spaceId}
                                loading={loading}
                                type={editingType}
                                onSave={saveInfoType}
                                onDelete={deleteInfoType}
                                onCancel={() => setEditingTypeId(undefined)}
                            />
                        </DialogContent>
                    </Dialog>
                )}
            </div>
        );
    }
}

const DisplayInfoTypes: FC<{
    spaceId: SpaceId;
    types: InfoType[];
    onAddNew: () => void;
    onEdit: (typeId: InfoTypeId) => void;
}> = ({ spaceId, types, onAddNew, onEdit }) => {
    return (
        <div className="space-y-2">
            <div className="flex flex-row justify-end">
                <Button onClick={onAddNew} variant="outline" size="sm" className="cursor-pointer">
                    Add Info Type
                </Button>
            </div>
            <div>
                <Table className="not-prose">
                    <TableHeader>
                        <TableRow>
                            <TableHead>ID</TableHead>
                            <TableHead>Data Type</TableHead>
                            <TableHead>Title</TableHead>
                            <TableHead>Help Text</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {types.map((type) => (
                            <TableRow
                                key={type.id}
                                className="group/row hover:bg-muted cursor-pointer"
                                onClick={() => onEdit(type.id)}
                            >
                                <TableCell className="flex flex-row gap-2 items-center">
                                    <InfoTypeDisplay spaceId={spaceId} typeId={type.id} />
                                </TableCell>
                                <TableCell>
                                    <FieldDataTypeBadge
                                        dataType={type.dataType}
                                        dimensionsSubType={type.dimensionsSubType}
                                        defaultLengthSelection={type.defaultLengthSelection}
                                        options={type.options}
                                    />
                                </TableCell>
                                <TableCell>{type.title}</TableCell>
                                <TableCell>{type.helpText}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
};
